import {
  faArrowDown,
  faArrowUp,
  faFolder,
  faPlusSquare,
  faRedo,
  faRotateLeft,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Chip from '@material-ui/core/Chip';
import Collapse from '@material-ui/core/Collapse';
import IconButton from '@material-ui/core/IconButton';
import AsyncTaskWrapper from 'CMFW/input/AsyncTaskWrapper';
import ModuleContext from 'CMFW/context/ModuleContext';
import { IChangeAwareProps } from 'components/common/styles';
import { IRefIds } from 'constants/refs';
import { getCacheKey } from 'helpers/cache';
import { useCacheData } from 'hooks/useCacheData';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { IAsyncTask } from 'types/asyncTask';
import VocCategoriesAddModal from '../modal/VocCategoriesAddModal';
import VocCategorySelectModel from '../../../models/VocCategorySelectModel';
import VocabularyModel from '../../../models/VocabularyModel';

const KEY = 'vocCategories';

const VocCategories: React.FC = () => {
  const moduleModel = useContext(ModuleContext);

  const asyncCategories = moduleModel.getItemValue(KEY) as IAsyncTask<number[]>;
  const modelCategories = asyncCategories.value;
  const [categories, setCategories] = useState(modelCategories);
  const [categoriesDeleted, setCategoriesDeleted] = useState([] as number[]);
  const [showModal, setShowModal] = useState(false);
  const [toggle, setToggle] = useState(false);

  const asyncCategoriesUpdated = moduleModel.getItemUpdatedValue(KEY) as IAsyncTask<number[]>;
  const modelValueUpdated = asyncCategoriesUpdated.value;

  const cacheKey = getCacheKey(IRefIds.vocabularyCategories);
  const vocCategoryData = useMemo(() => new VocCategorySelectModel(), []);
  const cacheSelectData = useCacheData(cacheKey, vocCategoryData.get.bind(vocCategoryData));

  // Check if categories are read-only (when vocabulary is a synonym child)
  const isReadonly = (moduleModel as VocabularyModel).isVocCategoriesReadonly();

  useEffect(() => {
    setCategories([...new Set([...modelValueUpdated, ...categoriesDeleted])]);
  }, [categoriesDeleted, modelValueUpdated]);

  useEffect(() => {
    setCategoriesDeleted([]);
  }, [modelCategories]);

  const handleAddClick = () => {
    setShowModal(true);
    setToggle(true);
  };

  const handleCloseAdd = () => {
    setShowModal(false);
  };

  const handleToggle = () => {
    setToggle(!toggle);
  };

  const handleRecreateClick = async () => {
    await (moduleModel as VocabularyModel).recreateCategories();
    // Force re-render by toggling state
    setToggle((prev) => !prev);
  };

  const handleAddCategory = (catId: number) => {
    // Use modelValueUpdated as source of truth to avoid state corruption
    const newCategories = modelValueUpdated.filter((id) => id !== catId);
    newCategories.push(catId);
    const updatedAsyncTask = {
      ...asyncCategories,
      value: newCategories,
    };
    moduleModel.updateItemValue.bind(moduleModel)(KEY, updatedAsyncTask);
    if (categoriesDeleted.indexOf(catId) > -1) {
      setCategoriesDeleted(categoriesDeleted.filter((id) => id !== catId));
    } else {
      setCategories(newCategories);
    }
  };

  const handleRemoveCategory = (catId: number) => {
    const isRestoringDeleted = categoriesDeleted.indexOf(catId) > -1;
    if (isRestoringDeleted) {
      handleAddCategory(catId);
      return;
    }

    // Use modelValueUpdated as source of truth to avoid state corruption
    const newCategories = modelValueUpdated.filter((cat) => cat !== catId);
    const updatedAsyncTask = {
      ...asyncCategories,
      value: newCategories,
    };
    moduleModel.updateItemValue.bind(moduleModel)(KEY, updatedAsyncTask);
    if (modelCategories.indexOf(catId) === -1) {
      setCategories(newCategories);
    } else {
      setCategoriesDeleted([...categoriesDeleted, catId]);
    }
  };

  const getCategoryName = (catId: number): string => {
    if (cacheSelectData !== null) {
      const cat = cacheSelectData.filter((cache) => cache.id === catId);
      if (cat.length === 1) {
        return cat[0].fullPath;
      }
    }

    return catId.toString();
  };

  const getCategoryShortName = (catId: number): string => {
    if (cacheSelectData !== null) {
      const cat = cacheSelectData.filter((cache) => cache.id === catId);
      if (cat.length === 1) {
        return cat[0].name;
      }
    }

    return catId.toString();
  };

  const categoriesSorted = categories.sort();

  return (
    <Wrapper>
      <Label>Categories</Label>
      <AsyncTaskWrapper task={asyncCategories} keyValue={KEY}>
        <Container>
          <ContainerTitle onClick={handleToggle} $clickable>
            {!isReadonly && (
              <ActionButtons onClick={(e) => e.stopPropagation()}>
                <IconButton size="small" onClick={handleAddClick} title="Add category">
                  <FontAwesomeIcon icon={faPlusSquare} size="sm" />
                </IconButton>
                <IconButton size="small" onClick={handleRecreateClick} title="Recreate categories">
                  <FontAwesomeIcon icon={faRedo} size="sm" />
                </IconButton>
              </ActionButtons>
            )}
            <Title>{!toggle && categories.map((id) => getCategoryShortName(id)).join(', ')}</Title>
            <ToggleIcon>
              <FontAwesomeIcon icon={toggle ? faArrowUp : faArrowDown} size="sm" />
            </ToggleIcon>
          </ContainerTitle>
          <Collapse in={toggle} timeout="auto">
            <ContentArea>
              {isReadonly && (
                <InfoMessage>Categories are inherited from parent synonym and cannot be edited</InfoMessage>
              )}
              <ChipsContainer>
                {categoriesSorted.map((cat) => {
                  const isNewCategory = modelCategories.indexOf(cat) === -1;
                  const isDeleted = categoriesDeleted.indexOf(cat) > -1;
                  return (
                    <CategoryChip
                      key={cat}
                      icon={<FontAwesomeIcon icon={faFolder} />}
                      label={getCategoryName(cat)}
                      onDelete={!isReadonly || isDeleted ? () => handleRemoveCategory(cat) : undefined}
                      $changed={isNewCategory}
                      $deleted={isDeleted}
                      variant={isDeleted ? 'default' : isNewCategory ? 'default' : 'outlined'}
                      deleteIcon={
                        isDeleted ? <FontAwesomeIcon icon={faRotateLeft} /> : <FontAwesomeIcon icon={faXmark} />
                      }
                    />
                  );
                })}
              </ChipsContainer>
            </ContentArea>
          </Collapse>
          {showModal && !isReadonly && (
            <VocCategoriesAddModal handleAddCategory={handleAddCategory} onClose={handleCloseAdd} />
          )}
        </Container>
      </AsyncTaskWrapper>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  gap: 8px;
`;

const Label = styled.div`
  font-size: 12px;
  font-weight: bold;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  border: solid 1px #e0e0e0;
  overflow: hidden;
`;

const ContainerTitle = styled.div<{ $clickable?: boolean }>`
  display: flex;
  flex-grow: 1;
  flex-direction: row;
  align-items: center;
  background-color: #f5f7f9;
  padding: 12px 16px;
  cursor: ${(props) => (props.$clickable ? 'pointer' : 'default')};
  transition: background-color 0.2s ease;
  user-select: none;

  &:hover {
    background-color: ${(props) => (props.$clickable ? '#ebeef1' : '#f5f7f9')};
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 4px;
  margin-right: 8px;
`;

const Title = styled.p`
  display: flex;
  flex-grow: 1;
  margin: 0;
  font-size: 14px;
  color: #555;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ToggleIcon = styled.div`
  display: flex;
  align-items: center;
  color: #666;
  margin-left: 8px;
  transition: transform 0.2s ease;
`;

const ContentArea = styled.div`
  padding: 16px;
`;

const ChipsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const CategoryChip = styled(Chip)<IChangeAwareProps & { $deleted: boolean }>`
  && {
    height: auto;
    justify-content: flex-start;
    background-color: ${(props) => {
      if (props.$deleted) return '#ffebee';
      if (props.$changed) return '#fff3e0';
      return 'transparent';
    }} !important;
    border-style: solid;
    border-color: ${(props) => {
      if (props.$deleted) return '#d0011b';
      if (props.$changed) return '#f6a623';
      return '#e0e0e0';
    }};
    border-width: 1px;
    border-radius: 16px;
    font-size: 0.9em;
    font-weight: normal;
    transition: all 0.2s ease;
    text-decoration: ${(props) => (props.$deleted ? 'line-through' : 'none')};
  }
  && .MuiChip-label {
    padding: 8px 12px;
    flex-grow: 1;
    text-align: left;
  }
  && .MuiChip-icon {
    margin-left: 8px;
    font-size: 16px;
    color: ${(props) => {
      if (props.$deleted) return '#d0011b';
      if (props.$changed) return '#f6a623';
      return '#666';
    }};
  }
  && .MuiChip-deleteIcon {
    margin-right: 8px;
    margin-left: 8px;
    font-size: 12px;
    width: 12px;
    height: 12px;
    color: ${(props) => {
      if (props.$deleted) return '#d0011b';
      return '#999';
    }};

    & svg {
      width: 12px;
      height: 12px;
    }

    &:hover {
      color: ${(props) => {
        if (props.$deleted) return '#d0011b';
        return '#d0011b';
      }};
    }
  }
`;

const InfoMessage = styled.div`
  padding: 12px 16px;
  margin-bottom: 16px;
  background-color: #e3f2fd;
  border-left: 4px solid #4990e2;
  color: #1565c0;
  font-size: 0.9em;
  border-radius: 4px;
`;

export default VocCategories;
